import React, { useState } from 'react';
import WeeklyScheduleReadOnly from '../components/zuweisung/WeeklyScheduleReadOnly';
import { praxisService } from '../services/praxis.service';
import { zuweisungService } from '../services/zuweisung.service';
import { abwesenheitService } from '../services/abwesenheit.service';
import { feiertagService } from '../services/feiertag.service';
import { scheduleService } from '../services/schedule.service';
import { exportWeeklyScheduleToPDF } from '../utils/pdfExport';
import { useEnvironment } from '../context/EnvironmentContext';
import { AppType, Zuweisung } from '../types';

const Dashboard: React.FC = () => {
  const { currentApp } = useEnvironment();
  const [isExporting, setIsExporting] = useState(false);
  const [selectedWeekStart, setSelectedWeekStart] = useState<Date>(getMonday(new Date()));
  const [includeEmptyCells] = useState(true); // Default to true, or could be a hidden setting

  function getMonday(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  }

  const handleExportPDF = async () => {
    try {
      setIsExporting(true);

      // Fetch data for the selected week
      const weekEnd = new Date(selectedWeekStart);
      weekEnd.setDate(weekEnd.getDate() + 5);

      let zuweisungen: Zuweisung[] = [];

      const [praxenData, abwesenheitenData, feiertageData] = await Promise.all([
        praxisService.getAllPraxen(currentApp as AppType),
        abwesenheitService.getAllAbwesenheiten({
          startDatum: selectedWeekStart,
          endDatum: weekEnd,
          app: currentApp as AppType
        }),
        feiertagService.getAllFeiertage({
          startDatum: selectedWeekStart,
          endDatum: weekEnd,
        }),
      ]);

      if (currentApp === AppType.LEGACY) {
        const response = await zuweisungService.getAllZuweisungen(
          {
            startDatum: selectedWeekStart,
            endDatum: weekEnd,
          },
          1,
          10000
        );
        zuweisungen = response.data;
      } else {
        zuweisungen = await scheduleService.getEffectiveSchedule({
          app: currentApp as AppType,
          startDatum: selectedWeekStart,
          endDatum: weekEnd
        });
      }

      // Determine Logo and Prefix based on App
      let logoUrl = '/logo_praxis_hayat_seite.webp';
      let filenamePrefix = '';

      if (currentApp === AppType.HAUSARZT) {
        logoUrl = '/hausarzt_logo.svg';
        filenamePrefix = 'Hausarztmedizin';
      } else if (currentApp === AppType.KINDERARZT) {
        logoUrl = '/kinder_logo.svg';
        filenamePrefix = 'Kindermedizin';
      }

      // Export to PDF
      const { doc, filename } = await exportWeeklyScheduleToPDF({
        weekStart: selectedWeekStart,
        praxen: praxenData,
        zuweisungen,
        abwesenheiten: abwesenheitenData,
        feiertage: feiertageData,
        includeEmptyCells,
        logoUrl,
        appTitle: 'Dienstplan', // Always generic per user request
        filenamePrefix
      });

      doc.save(filename);

    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Fehler beim Exportieren des PDFs');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Dashboard</h1>

      {/* Weekly Schedule Section */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
          <h2 className="text-2xl font-semibold text-gray-800">Wochenübersicht Zuweisungen</h2>

          {/* PDF Export Button */}
          <div className="flex gap-2">
            <button
              onClick={handleExportPDF}
              disabled={isExporting}
              className="px-4 py-2 min-h-[44px] bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {isExporting ? 'Exportiere...' : 'Als PDF exportieren'}
            </button>
          </div>
        </div>

        <WeeklyScheduleReadOnly
          weekStart={selectedWeekStart}
          onWeekChange={setSelectedWeekStart}
        />
      </div>
    </div>
  );
};

export default Dashboard;

